/**
 * DCF 가치 평가 카드 — 종목 상세 드로어용 쿼리 내장형 공통 컴포넌트
 * (StockCandleChart 4종 패턴 승계 — query 내장, enabled prop)
 *
 * BE: GET /v1/valuation/dcf (재무 데이터 일 1회 캐시 — 첫 조회만 수 초, 이후 즉시)
 * 표시 전용 지표 — 매매 판정 룰과 무관 (캡틴 승인 2026-10-31)
 */
import { Card, Spin } from "antd";
import { useQuery } from "@tanstack/react-query";
import { valuationApi, type DcfResult } from "src/api/valuation";

interface DcfValuationCardProps {
  stockId: string;
  overseas: boolean;
  enabled?: boolean;
}

/** ETF·데이터 부재류는 카드 자체를 숨김 (보유종목 대부분이 ETF라 노이즈 방지) */
const HIDDEN_REASON_PATTERNS = [
  "현금흐름표 데이터 없음",
  "현금흐름표 필드",
  "yfinance 조회 실패",
  "현재가 조회 실패",
  "발행주식수",
];

const isHiddenReason = (r: DcfResult) =>
  !r.applicable && HIDDEN_REASON_PATTERNS.some((p) => (r.reason ?? "").includes(p));

const fmtPrice = (v: number | null, overseas: boolean) => {
  if (v == null) return "-";
  return overseas
    ? `$${v.toLocaleString("en-US", { maximumFractionDigits: 2 })}`
    : `${Math.round(v).toLocaleString("ko-KR")}원`;
};

const DcfValuationCard = ({ stockId, overseas, enabled = true }: DcfValuationCardProps) => {
  const q = useQuery({
    queryKey: ["valuation", "dcf", stockId],
    queryFn: () => valuationApi.getDcf([stockId]).then((m) => m[stockId] ?? null),
    enabled,
    staleTime: 60 * 60_000, // BE도 일 1회 캐시 — FE는 1시간
    retry: 1,
  });

  if (!enabled || !q.data) {
    if (q.isLoading && enabled) {
      return (
        <Card size="small" title="가치 평가 (DCF)" style={{ marginBottom: 12 }}>
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Spin size="small" /> <span style={{ fontSize: 12, color: "#999" }}>재무 데이터 조회 중 (최초 1회 수 초)</span>
          </div>
        </Card>
      );
    }
    return null;
  }

  const r = q.data;
  if (isHiddenReason(r)) return null;

  // ── DCF 무효: 사유 + 대체 지표 (금융·보험주, FCF 적자 등) ──
  if (!r.applicable) {
    const alt = r.alternative;
    return (
      <Card size="small" title="가치 평가 (DCF)" style={{ marginBottom: 12 }} styles={{ body: { padding: "10px 14px" } }}>
        <div style={{ fontSize: 12, color: "#999", marginBottom: alt ? 8 : 0 }}>⚠ {r.reason}</div>
        {alt && (alt.pbr != null || alt.dividend_yield_pct != null || alt.per != null) && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 6,
              fontSize: 13,
            }}
          >
            <div>
              <div style={{ fontSize: 11, opacity: 0.55 }}>PER (대체)</div>
              <div style={{ fontWeight: 600 }}>{alt.per != null ? alt.per.toFixed(2) : "-"}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.55 }}>PBR (대체)</div>
              <div style={{ fontWeight: 600 }}>{alt.pbr != null ? alt.pbr.toFixed(2) : "-"}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.55 }}>배당수익률</div>
              <div style={{ fontWeight: 600 }}>{alt.dividend_yield_pct != null ? `${alt.dividend_yield_pct.toFixed(2)}%` : "-"}</div>
            </div>
          </div>
        )}
      </Card>
    );
  }

  // ── DCF 정상 ──
  const margin = r.safety_margin_pct ?? 0;
  const marginClass = margin > 0 ? "fred" : margin < 0 ? "fblue" : "";
  const g1 = r.growth_stage1 != null ? `${(r.growth_stage1 * 100).toFixed(1)}%` : "-";
  const eps = r.kis_reference?.eps;

  return (
    <Card
      size="small"
      title="가치 평가 (DCF)"
      extra={<span style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>표시 전용</span>}
      style={{ marginBottom: 12 }}
      styles={{ body: { padding: "10px 14px" } }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 4 }}>
        <span style={{ fontSize: 12, color: "#999" }}>안전마진 (내재가치 대비)</span>
        <span className={marginClass} style={{ fontSize: 18, fontWeight: 700 }}>
          {margin > 0 ? "+" : ""}
          {margin.toFixed(1)}%
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 6,
          fontSize: 13,
          marginTop: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 11, opacity: 0.55 }}>현재가</div>
          <div style={{ fontWeight: 600 }}>{fmtPrice(r.current_price, overseas)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.55 }}>내재가치</div>
          <div style={{ fontWeight: 600 }}>{fmtPrice(r.intrinsic_value, overseas)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.55 }}>EPS (KIS){r.kis_reference?.eps_date ? ` ${r.kis_reference.eps_date.slice(5).replace("-", ".")}` : ""}</div>
          <div style={{ fontWeight: 600 }}>{eps != null ? fmtPrice(eps, overseas) : "-"}</div>
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#999", marginTop: 8 }}>
        가정: 할인율 9% · 명시적 성장 {g1} (5년) · 영구성장 2.5% · FCF = 최대 5년 평균 (영업현금흐름+CAPEX)
      </div>
      {r.warning && (
        <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 8, background: "rgba(230, 162, 60, 0.12)", fontSize: 12, color: "#e6a23c" }}>
          ⚠ {r.warning}
        </div>
      )}
    </Card>
  );
};

export default DcfValuationCard;
